import React, { useState } from "react";
import { Header } from "../../Header";
import './OnlineOrderCollections.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Typography from '@mui/material/Typography';

// Level 2: Collections sub-table row
function OrderRow({ row }) {
    const [open, setOpen] = useState(false);
    const hasCollections = row.collections && row.collections.length > 0;

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }} className="ooc-row-main">
                <TableCell style={{ width: '40px' }}>
                    {hasCollections && (
                        <IconButton size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell>{row.AreaName || '-'}</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>{row.SaleOrderID}</TableCell>
                <TableCell>{row.CollectionBranchId || '-'}</TableCell>
                <TableCell>
                    <div style={{ fontSize: '11px', color: '#555' }}>{row.TransTypeId || '-'}</div>
                    <div style={{ fontSize: '10px', color: '#888' }}>{row.TranSubTypeId || '-'}</div>
                </TableCell>
                <TableCell>
                    {row.OrderDate ? dayjs(row.OrderDate).format('DD-MM-YYYY HH:mm') : '-'}
                </TableCell>
                <TableCell>
                    <span className={`ooc-status-badge ${row.OrderStatusId || ''}`}>
                        {row.OrderStatusId || '-'}
                    </span>
                </TableCell>
                <TableCell>
                    <div style={{ fontWeight: 600, fontSize: '12px' }}>{row.PaymentGatewayName || '-'}</div>
                    <div style={{ fontSize: '10px', color: '#888' }}>{row.PaymentMethodId || '-'}</div>
                </TableCell>
                <TableCell className="ooc-currency" align="right">{row.Amount}</TableCell>
            </TableRow>

            {hasCollections && (
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box className="ooc-expanded-area">
                                <Typography className="ooc-section-title">Collection Details</Typography>
                                <Table size="small" className="ooc-level2-table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Ref No</TableCell>
                                            <TableCell>Business Date</TableCell>
                                            <TableCell>Gateway</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {row.collections.map((col, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{col.refNo || '-'}</TableCell>
                                                <TableCell>
                                                    {col.BusinessDate ? dayjs(col.BusinessDate).format('DD-MM-YYYY') : '-'}
                                                </TableCell>
                                                <TableCell>{col.PaymentGatewayName || col.PaymentGatewayID || '-'}</TableCell>
                                                <TableCell align="right" style={{ fontWeight: 600, color: '#1b5e20' }}>
                                                    {col.Amount}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </React.Fragment>
    );
}

export const OnlineOrderCollections = () => {
    const token = localStorage.getItem('AuthorizationToken');

    const [fromDate, setFromDate] = useState(dayjs().startOf('month'));
    const [toDate, setToDate] = useState(dayjs());
    const [subType, setSubType] = useState('ALL');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [filterText, setFilterText] = useState('');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 100;

    const handleAuthError = () => {
        setError("Authentication error. Please log in again.");
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
    };

    const fetchReportData = async () => {
        if (!fromDate || !toDate) {
            setError('Please select both Start Date and End Date.');
            return;
        }
        if (fromDate.isAfter(toDate)) {
            setError('Start Date cannot be after End Date.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const startDateStr = fromDate.format('YYYYMMDD');
            const endDateStr = toDate.format('YYYYMMDD');

            const url = `https://tpapi.abisaio.com:3060/api/v1/getjsononlineordercollections?Areaid=00001&SubType=${subType}&FromDate=${startDateStr}&Todate=${endDateStr}`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    handleAuthError();
                    return;
                }
                throw new Error(`Server responded with status: ${response.status}`);
            }

            const jsonResponse = await response.json();

            if (jsonResponse && jsonResponse.data && Array.isArray(jsonResponse.data.OnlineReceipts)) {
                setPage(1);
                setReportData(jsonResponse.data.OnlineReceipts);
                if (jsonResponse.data.OnlineReceipts.length === 0) {
                    setError("No data available for the selected filters.");
                }
            } else {
                console.error("Unexpected data format:", jsonResponse);
                setError("Received invalid data format from server.");
                setReportData([]);
            }
        } catch (err) {
            console.error("Error fetching online order collections:", err);
            setError(`Failed to fetch report data. (${err.message})`);
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = reportData.filter(item => {
        if (!filterText) return true;
        const s = filterText.toLowerCase();
        return (
            (item.SaleOrderID && item.SaleOrderID.toLowerCase().includes(s)) ||
            (item.AreaName && item.AreaName.toLowerCase().includes(s)) ||
            (item.CollectionBranchId && item.CollectionBranchId.toLowerCase().includes(s)) ||
            (item.PaymentGatewayName && item.PaymentGatewayName.toLowerCase().includes(s))
        );
    });

    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
    const pagedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const totalAmount = filteredData.reduce((sum, r) => sum + (r.Amount || 0), 0);

    const exportToExcel = () => {
        if (filteredData.length === 0) {
            setError("No data to export.");
            return;
        }

        try {
            const rows = [];

            filteredData.forEach(item => {
                const baseRow = {
                    'Area Name': item.AreaName || '',
                    'Sale Order ID': item.SaleOrderID || '',
                    'Branch ID': item.CollectionBranchId || '',
                    'Trans Type': item.TransTypeId || '',
                    'Sub Type': item.TranSubTypeId || '',
                    'Order Date': item.OrderDate ? dayjs(item.OrderDate).format('DD-MM-YYYY HH:mm:ss') : '',
                    'Order Status': item.OrderStatusId || '',
                    'Payment Gateway': item.PaymentGatewayName || '',
                    'Payment Method': item.PaymentMethodId || '',
                    'Order Amount': item.Amount || 0,
                };

                const collections = item.collections || [];
                if (collections.length === 0) {
                    rows.push({
                        ...baseRow,
                        'Collection Ref No': '',
                        'Collection Business Date': '',
                        'Collection Gateway': '',
                        'Collection Amount': 0,
                    });
                } else {
                    collections.forEach(col => {
                        rows.push({
                            ...baseRow,
                            'Collection Ref No': col.refNo || '',
                            'Collection Business Date': col.BusinessDate ? dayjs(col.BusinessDate).format('DD-MM-YYYY') : '',
                            'Collection Gateway': col.PaymentGatewayName || col.PaymentGatewayID || '',
                            'Collection Amount': col.Amount || 0,
                        });
                    });
                }
            });

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Online Order Collections");

            const fileName = `OnlineOrderCollections_${dayjs().format('DDMMYYYY')}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        } catch (err) {
            console.error("Error exporting Excel file:", err);
            setError("Failed to export Excel file.");
        }
    };

    const Loader = () => (
        <div className="ooc-loader-container">
            <div className="ooc-loader"></div>
            <p className="ooc-loader-text">Loading...</p>
        </div>
    );

    return (
        <>
            <Header />
            <div className="ooc-container">
                {loading && <Loader />}

                <h2 className="ooc-title">Online Order Collections Report</h2>

                <div className="ooc-controls">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="From Date"
                            value={fromDate}
                            onChange={(val) => setFromDate(val)}
                            format="DD-MM-YYYY"
                            slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                        />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="To Date"
                            value={toDate}
                            onChange={(val) => setToDate(val)}
                            format="DD-MM-YYYY"
                            slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                        />
                    </LocalizationProvider>

                    <select
                        value={subType}
                        onChange={(e) => setSubType(e.target.value)}
                        className="ooc-select"
                    >
                        <option value="ALL">All Sub Types</option>
                        <option value="WEBORDER">Web Order</option>
                        <option value="APPORDER">App Order</option>
                    </select>

                    <div className="ooc-search">
                        <input
                            type="text"
                            value={filterText}
                            onChange={(e) => { setFilterText(e.target.value); setPage(1); }}
                            placeholder="Search Order ID, Area, Branch, Gateway..."
                            className="ooc-search-field"
                        />
                    </div>

                    <div className="ooc-buttons">
                        <button onClick={fetchReportData} className="ooc-button ooc-load-button" disabled={loading}>
                            Fetch Report
                        </button>
                        <button onClick={exportToExcel} className="ooc-button ooc-export-button" disabled={loading || filteredData.length === 0}>
                            Excel Export
                        </button>
                    </div>
                </div>

                {error && <div className="ooc-error-message">{error}</div>}

                {filteredData.length > 0 && (
                    <div className="ooc-summary-bar">
                        <div className="ooc-summary-item">Records: <span>{filteredData.length}</span></div>
                        <div className="ooc-summary-item">Total Amount: <span>₹ {totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                        <div className="ooc-summary-item" style={{ marginLeft: 'auto' }}>
                            Page <span>{page}</span> of <span>{totalPages}</span>
                            &nbsp;({(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredData.length)} of {filteredData.length})
                        </div>
                    </div>
                )}

                <TableContainer component={Paper} className="ooc-table-container" sx={{ maxHeight: 'calc(100vh - 290px)' }}>
                    <Table stickyHeader aria-label="online order collections table" className="ooc-report">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: '40px' }} />
                                <TableCell>Area</TableCell>
                                <TableCell>Sale Order ID</TableCell>
                                <TableCell>Branch</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Order Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Payment</TableCell>
                                <TableCell align="right">Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pagedData.length > 0 ? (
                                pagedData.map((row, idx) => (
                                    <OrderRow key={`${row.SaleOrderID}-${idx}`} row={row} />
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" style={{ padding: '40px', color: '#666', fontStyle: 'italic' }}>
                                        {reportData.length === 0 && !error
                                            ? "Select dates and click 'Fetch Report' to load data"
                                            : "No data available matching filters"}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {totalPages > 1 && (
                    <div className="ooc-pagination">
                        <button
                            className="ooc-page-btn"
                            onClick={() => setPage(1)}
                            disabled={page === 1}
                        >«</button>
                        <button
                            className="ooc-page-btn"
                            onClick={() => setPage(p => p - 1)}
                            disabled={page === 1}
                        >‹</button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                            .reduce((acc, p, i, arr) => {
                                if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                                acc.push(p);
                                return acc;
                            }, [])
                            .map((p, i) =>
                                p === '...'
                                    ? <span key={`ellipsis-${i}`} className="ooc-page-ellipsis">…</span>
                                    : <button
                                        key={p}
                                        className={`ooc-page-btn${page === p ? ' active' : ''}`}
                                        onClick={() => setPage(p)}
                                    >{p}</button>
                            )
                        }

                        <button
                            className="ooc-page-btn"
                            onClick={() => setPage(p => p + 1)}
                            disabled={page === totalPages}
                        >›</button>
                        <button
                            className="ooc-page-btn"
                            onClick={() => setPage(totalPages)}
                            disabled={page === totalPages}
                        >»</button>
                    </div>
                )}
            </div>
        </>
    );
};
